import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import { ChartBaseProps } from '../types/chart-types';
import { t } from 'i18next';

// Initialize the no-data module for displaying messages when no data is available
NoDataToDisplay(Highcharts);

/**
 * Props interface for LivisSplineChart component
 * Extends ChartBaseProps with spline chart specific configuration options
 */
interface LivisSplineChartProps extends ChartBaseProps {
  height?: number; // Chart height in pixels
  categories?: string[]; // X-axis category labels for categorical data
  markerConfig?: { // Configuration for data point markers
    enabled?: boolean; // Whether to show markers on data points
    symbol?: string; // Marker shape (circle, square, diamond, etc.)
    fillColor?: string; // Interior color of markers
    lineColor?: string | null; // Border color of markers
    lineWidth?: number; // Border width of markers
    radius?: number; // Size of markers in pixels
  };
  noDataMessage?: string; // Message to display when no data is available
  noDataStyle?: { // Styling for the no-data message
    fontSize?: string; // Font size for no-data text
    color?: string; // Text color for no-data message
  };
}

/**
 * LivisSplineChart - A customizable spline chart component built on Highcharts
 * 
 * This component provides smooth curve line chart visualization ideal for showing
 * trends and patterns in continuous data. Spline charts use curved lines instead
 * of straight line segments, creating a more visually appealing and smoother
 * representation of data trends.
 * 
 * Features:
 * - Smooth spline curves for elegant data visualization
 * - Configurable data point markers with customizable styling
 * - Support for both categorical and continuous X-axis data
 * - Interactive tooltips with crosshairs for precise data reading
 * - Flexible data input (data or series props)
 * - Built-in no-data handling with customizable messages
 * - Responsive design with adjustable height
 * - Click event handling for interactive dashboards
 * - Number formatting with thousand separators
 * 
 * @param props - Component configuration and data
 * @returns Rendered Highcharts spline chart component
 */
const LivisSplineChart: React.FC<LivisSplineChartProps> = ({
  data = [], // Primary data array (legacy support)
  series = [], // Preferred data input as series array
  title = '', // Main chart title
  subtitle = '', // Chart subtitle for additional context
  x_title = '', // X-axis label/title
  y_title = '', // Y-axis label/title
  tooltip = true, // Whether to show tooltips on hover
  legend = false, // Whether to display the legend
  onClickPoint, // Callback function for point click events
  x_range, // X-axis range limits [min, max]
  y_range, // Y-axis range limits [min, max]
  height = 300, // Chart height in pixels
  categories, // X-axis category labels for discrete data
  markerConfig = { // Default marker configuration
    enabled: true,
    symbol: 'circle',
    fillColor: 'var(--livis-white-color)',
    lineColor: null,
    lineWidth: 2,
    radius: 6,
  },
  noDataMessage = 'No data available', // Default no-data message
  noDataStyle = { // Default no-data styling
    fontSize: 'var(--livis-font-size-xs1)',
    color: 'var(--livis-gray-font-color)',
  },
  plotOptions, // Additional Highcharts plot options
}) => {
  /**
   * Configure global Highcharts settings
   * Sets up number formatting with thousand separators for better readability
   */
  Highcharts.setOptions({
    lang: {
      thousandsSep: ',', // Use comma as thousand separator (e.g., 1,000)
    },
  });

  /**
   * Determine chart data source with priority handling
   * Prioritizes 'series' prop over 'data' prop for flexibility
   * This allows backward compatibility while encouraging the more structured 'series' format
   */
  const chartSeries = series?.length > 0 ? series : (data || []);

  /**
   * Comprehensive Highcharts configuration object
   * Defines all visual and behavioral aspects of the spline chart
   */
  const options = {
    // Chart container and type configuration
    chart: {
      type: 'spline', // Smooth curved line chart type
      height: height, // Use provided height
    },
    
    // Chart titles
    title: {
      text: title, // Main chart title
      style: { fontSize: 'var(--livis-font-size-base)' }
    },
    subtitle: {
      text: subtitle, // Additional context below title
      style: { fontSize: 'var(--livis-font-size-xs)' }
    },
    
    // X-axis configuration (horizontal axis)
    xAxis: {
      ...(categories && { categories }),
      title: {
        text: x_title,
        style: { fontSize: 'var(--livis-font-size-xs)' }
      },
      ...(x_range && { min: x_range[0], max: x_range[1] }),
      lineWidth: categories ? 0 : 1,
      tickWidth: categories ? 0 : 1,
      labels: {
        style: { fontSize: 'var(--livis-font-size-xs)' }
      }
    },
    
    // Y-axis configuration (vertical axis)
    yAxis: {
      title: {
        text: y_title,
        style: { fontSize: 'var(--livis-font-size-xs)' }
      },
      ...(y_range && { min: y_range[0], max: y_range[1] }),
      labels: {
        style: { fontSize: 'var(--livis-font-size-xs)' }
      }
    },
    
    // Legend configuration
    legend: {
      enabled: legend, // Show/hide legend based on prop
    },
    
    // Enhanced tooltip configuration for spline charts
    tooltip: {
      enabled: tooltip, // Enable/disable tooltips
      shared: true, // Show data for all series at once
      crosshairs: true, // Display crosshair lines for precise reading
    },
    
    // Disable export functionality for cleaner interface
    exporting: {
      enabled: false, // Remove export menu
    },
    
    // Remove Highcharts branding
    credits: {
      enabled: false, // Hide Highcharts watermark
    },
    
    // Plot options defining chart behavior and styling
    plotOptions: {
      spline: {
        marker: markerConfig, // Apply marker configuration
        point: {
          events: {
            /**
             * Point click event handler
             * Provides click functionality for interactive dashboards
             * Passes the full point object to the callback for maximum flexibility
             */
            click: function () {
              if (onClickPoint) {
                onClickPoint(this); // 'this' refers to the clicked point
              }
            },
          },
        },
        // Merge any additional spline-specific plot options
        ...plotOptions?.spline,
      },
      // Spread any additional plot options for other chart elements
      ...plotOptions,
    },
    
    // Data series for the chart
    series: chartSeries,
    
    // No-data handling configuration
    lang: {
      noData: `${t(noDataMessage)}`, // Message when no data is available
    },
    noData: {
      style: noDataStyle, // Styling for no-data message
    },
  };

  /**
   * Render the Highcharts component
   * Uses HighchartsReact wrapper for React integration
   */
  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

/**
 * PropTypes validation for development and debugging
 * Ensures proper data types and identifies missing or incorrect props
 */
LivisSplineChart.propTypes = {
  data: PropTypes.array, // Legacy data input format
  series: PropTypes.array, // Preferred series data format
  title: PropTypes.string,
  subtitle: PropTypes.string,
  x_title: PropTypes.string,
  y_title: PropTypes.string,
  tooltip: PropTypes.bool,
  legend: PropTypes.bool,
  onClickPoint: PropTypes.func, // Callback for point interactions
  x_range: PropTypes.array, // X-axis range as [min, max]
  y_range: PropTypes.array, // Y-axis range as [min, max]
  height: PropTypes.number,
  categories: PropTypes.array, // X-axis category labels
  markerConfig: PropTypes.object, // Marker styling configuration
  noDataMessage: PropTypes.string, // Custom no-data message
  noDataStyle: PropTypes.object, // No-data message styling
  plotOptions: PropTypes.object, // Additional Highcharts options
};

/**
 * Default props configuration
 * Provides sensible defaults for all optional props to ensure the component
 * works out-of-the-box while remaining highly customizable
 */
LivisSplineChart.defaultProps = {
  data: [], // Empty array prevents errors
  series: [], // Empty array prevents errors
  title: '', // No title by default for flexible usage
  subtitle: '', // No subtitle by default
  x_title: '', // No axis titles by default
  y_title: '',
  tooltip: true, // Tooltips enabled by default for better UX
  legend: false, // Legend disabled by default for cleaner appearance
  onClickPoint: undefined, // No click handler by default
  x_range: undefined, // No range limits by default (auto-scaling)
  y_range: undefined, // No range limits by default (auto-scaling)
  height: 300, // Standard height suitable for most dashboard components
  categories: undefined, // No categories by default (continuous X-axis)
  
  // Default marker configuration for appealing visual design
  markerConfig: {
    enabled: true, // Show markers by default
    symbol: 'circle', // Classic circular markers
    fillColor: '#fff', // White fill for good contrast
    lineColor: null, // Let Highcharts choose border color
    lineWidth: 2, // Moderate border width
    radius: 6, // Medium-sized markers for good visibility
  },
  
  // Default no-data configuration
  noDataMessage: 'No data available', // Standard message
  noDataStyle: {
    fontSize: 'var(--livis-font-size-xs1)', // Slightly smaller than normal text
    color: '#606060', // Subtle gray color
  },
  
  plotOptions: undefined, // No additional plot options by default
};

export default LivisSplineChart;
